import { createSelector } from '@reduxjs/toolkit';
import { calculateScheduledWorkDay, employeesNotDeleted, timeInMinutes, today } from 'src/store/selectors';
import type { EmployeePresenceStatus } from 'src/store/selectors/staffing/employee-presence-status';
import { LocalDate } from 'src/utilities/local-date';
import type { EmployeeViewModelWithName } from 'src/store/types';
import { sortBy } from 'lodash';

function getWorkDayMinutes(
  employee: EmployeeViewModelWithName,
  day: string,
  dayDate: LocalDate,
): { start: number; end: number } | undefined {
  const exception = employee.workScheduleExceptions.find((ex) => {
    return LocalDate.fromString(ex.date).isSame(dayDate);
  });
  if (exception && exception.work && exception.start && exception.end) {
    return {
      start: timeInMinutes(exception.start),
      end: timeInMinutes(exception.end),
    };
  } else {
    const scheduledWorkDay = calculateScheduledWorkDay(employee.workSchedules, day);
    if (scheduledWorkDay.workDay) {
      return {
        start: timeInMinutes(scheduledWorkDay.start),
        end: timeInMinutes(scheduledWorkDay.end),
      };
    }
  }
  return undefined;
}

function getLeaveDayMinutes(
  employee: EmployeeViewModelWithName,
  dayDate: LocalDate,
): { start: number; end: number } | undefined {
  const leaveToday = employee.leavePeriods.find((l) => {
    return LocalDate.fromString(l.start).isSameOrBefore(dayDate) && LocalDate.fromString(l.end).isSameOrAfter(dayDate);
  });
  if (leaveToday) {
    let start = 0;
    let end = 1440;
    if (LocalDate.fromString(leaveToday.start).isSame(dayDate) && leaveToday.startTime) {
      start = timeInMinutes(leaveToday.startTime);
    }
    if (LocalDate.fromString(leaveToday.end).isSame(dayDate) && leaveToday.endTime) {
      end = timeInMinutes(leaveToday.endTime);
    }
    return { start, end };
  } else {
    return undefined;
  }
}

function isPresent(employee: EmployeeViewModelWithName, day: string, dayDate: LocalDate): boolean {
  const workDay = getWorkDayMinutes(employee, day, dayDate);
  if (!workDay) {
    return false;
  } else {
    const leaveDay = getLeaveDayMinutes(employee, dayDate);
    if (!leaveDay) {
      return true;
    } else {
      if (leaveDay.start === 0 && leaveDay.end === 1440) {
        return false;
      } else {
        return workDay.start < leaveDay.start || workDay.end > leaveDay.end;
      }
    }
  }
}

export const getEmployeesPresenceStatus = createSelector(
  employeesNotDeleted,
  today,
  (employees: EmployeeViewModelWithName[], today: string): EmployeePresenceStatus[] => {
    const todayDate = LocalDate.fromString(today);
    const result = employees
      .filter((employee) => employee.status === 'ACTIVE')
      .map((employee) => {
        let profession = employee.profession;
        if (!profession || profession === 'Annet') {
          profession = '';
        }
        return {
          uuid: employee.uuid,
          name: employee.name,
          profession,
          present: isPresent(employee, today, todayDate),
        };
      });
    return sortBy(result, [(item) => item.name]);
  },
);
